import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { buildZoneColorMap, zoneColorFrom } from '../../../utils/zoneColors';
import { dedupeClosingPoints, resolveFramePoints } from '../../../utils/polygon';

/**
 * Обзор всех зон камеры на одном кадре (read-only). До этого существующие
 * зоны можно было увидеть только по одной и только через «Изменить».
 * Цвет зоны — по zone_id (utils/zoneColors), тот же, что в наложении на
 * обработанные кадры и у пунктов списка.
 * Координаты — пиксели original-кадра (FE-Z01); sentinel «весь кадр»
 * разворачивается по natural-размеру (resolveFramePoints).
 */
const drawZone = (ctx, zone, index, colorMap) => {
  const pts = resolveFramePoints(dedupeClosingPoints(zone?.polygon), ctx.canvas.width, ctx.canvas.height);
  if (pts.length < 2) return;
  const color = zoneColorFrom(colorMap, zone, index);

  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  pts.slice(1).forEach(([x, y]) => ctx.lineTo(x, y));
  if (pts.length > 2) ctx.closePath();
  ctx.lineWidth = 3;
  ctx.strokeStyle = color.stroke;
  ctx.stroke();
  if (pts.length > 2) { ctx.fillStyle = color.fill; ctx.fill(); }

  // Подпись внутри кадра: у sentinel-зоны первая точка — угол (0,0), текст
  // над ней вылетел бы за холст.
  const label = zone?.display_name || zone?.zone_id || `Зона ${index + 1}`;
  const x = Math.min(pts[0][0] + 8, ctx.canvas.width - 8);
  const y = Math.max(pts[0][1] - 8, 20);
  ctx.font = 'bold 18px Arial';
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(255,255,255,0.85)';
  ctx.strokeText(label, x, y);
  ctx.fillStyle = color.stroke;
  ctx.fillText(label, x, y);
};

const ZonesOverview = ({ url, zones = [] }) => {
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  const colorMap = useMemo(() => buildZoneColorMap(zones), [zones]);

  const draw = useCallback(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas || !image.naturalWidth) return;
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);
    zones.forEach((zone, index) => drawZone(ctx, zone, index, colorMap));
  }, [zones, colorMap]);

  // Список зон меняется после сохранения/удаления — кадр тот же, перерисовка.
  useEffect(draw, [draw, url]);

  return (
    <div className="vio-zone-canvas vio-zone-overview">
      <img ref={imageRef} src={url} alt="Кадр камеры с зонами" onLoad={draw} style={{ display: 'none' }} />
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ZonesOverview;
